import cn from 'classnames';
import { FC, useCallback, useEffect, useState } from 'react';

import { LoadingSpinner } from '@components/uiKit/LoadingSpinner';

import CrossIcon from '@icons/16/close.svg';

import styles from './PhoneEmailInput.module.scss';
import { FieldType, PhoneEmailInputProps } from './types';
import {
    checkPhoneLength,
    determineFieldType,
    determinePhonePrefix,
    formatPhoneNumberWithMask,
    getDefaultLabelFromUtils,
    getInputTypeFromUtils,
    isLikelyAutofill,
    isPhoneFormat,
} from './utils';

const MAX_PHONE_DIGITS = 11;

const getDigits = (value: string) => value.replace(/\D/g, '');

const PhoneEmailInput: FC<PhoneEmailInputProps> = ({
    name,
    value,
    onChange,
    error = false,
    isLoading = false,
    autoFocus = false,
    theme = 'primary',
    fieldType: forcedFieldType = FieldType.AUTO,
    disabled = false,
    onClearError,
    withoutLabel,
}) => {
    const [inputValue, setInputValue] = useState(value || '');
    const [phoneDigits, setPhoneDigits] = useState('');
    const [fieldType, setFieldType] = useState<FieldType>(FieldType.EMAIL);

    const setPhoneDigitsIfEmpty = (nextValue: string) => {
        const newPrefix = determinePhonePrefix(nextValue, phoneDigits);
        if (newPrefix !== '') {
            setPhoneDigits(newPrefix);
        }
    };

    const handlePhoneInput = (nextValue: string) => {
        setFieldType(FieldType.PHONE);
        const formatted = formatPhoneNumberWithMask(nextValue, fieldType);
        setInputValue(formatted);
        const cleanValue = nextValue.replace(/\D/g, '');
        onChange(cleanValue);
    };

    const setEmailType = (nextValue?: string) => {
        setFieldType(FieldType.EMAIL);
        if (nextValue !== undefined) {
            setInputValue(nextValue);
            onChange(nextValue);
        }
    };

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value;

            if (onClearError) {
                onClearError();
            }

            if (newValue === '') {
                if (forcedFieldType === 'auto') {
                    setPhoneDigits('');
                    setFieldType(FieldType.EMAIL);
                }
                setInputValue('');
                onChange('');

                return;
            }

            if (newValue === '+') {
                if (forcedFieldType === FieldType.PHONE || forcedFieldType === FieldType.CONTACT_PHONE) {
                    setFieldType(FieldType.PHONE);
                    setInputValue('+');
                    onChange('');

                    return;
                } else if (forcedFieldType === FieldType.EMAIL || forcedFieldType === FieldType.CONTACT_EMAIL) {
                    return;
                }
                setFieldType(FieldType.EMAIL);
                setInputValue('+');
                onChange('');

                return;
            }

            const isAutofill = isLikelyAutofill(newValue, inputValue);

            if (isAutofill) {
                if (forcedFieldType === FieldType.PHONE || forcedFieldType === FieldType.CONTACT_PHONE) {
                    if (!/[^0-9+\-()\s]/.test(newValue)) {
                        if (!checkPhoneLength(newValue, fieldType)) {
                            return;
                        }
                        handlePhoneInput(newValue);
                        setPhoneDigitsIfEmpty(newValue);
                    }

                    return;
                }

                if (forcedFieldType === FieldType.EMAIL || forcedFieldType === FieldType.CONTACT_EMAIL) {
                    setEmailType(newValue);

                    return;
                }

                if (isPhoneFormat(newValue)) {
                    if (!checkPhoneLength(newValue, fieldType)) {
                        return;
                    }
                    handlePhoneInput(newValue);
                    setPhoneDigitsIfEmpty(newValue);

                    return;
                } else if (newValue.length > 0 && !/[^0-9+\-()\s]/.test(newValue)) {
                    const digits = newValue.replace(/\D/g, '');
                    if (!checkPhoneLength(newValue, fieldType)) {
                        return;
                    }
                    if (isPhoneFormat(newValue)) {
                        handlePhoneInput(newValue);
                        setPhoneDigitsIfEmpty(digits);

                        return;
                    }
                    setEmailType(newValue);

                    return;
                }
                setEmailType(newValue);

                return;
            }

            if (forcedFieldType === FieldType.PHONE || forcedFieldType === FieldType.CONTACT_PHONE) {
                if (!/[^0-9+\-()\s]/.test(newValue)) {
                    if (!checkPhoneLength(newValue, fieldType)) {
                        return;
                    }
                    handlePhoneInput(newValue);
                }

                return;
            }

            if (forcedFieldType === FieldType.EMAIL || forcedFieldType === FieldType.CONTACT_EMAIL) {
                setEmailType(newValue);

                return;
            }

            if (fieldType === FieldType.PHONE || fieldType === FieldType.CONTACT_PHONE) {
                const hasNonPhoneChars = /[^0-9+\-()\s]/.test(newValue);
                const isDeleting = newValue.length < inputValue.length;

                if (hasNonPhoneChars) {
                    setEmailType(newValue);

                    return;
                }

                if (newValue === '') {
                    setPhoneDigits('');
                    setInputValue('');
                    onChange('');
                    setFieldType(FieldType.EMAIL);

                    return;
                }

                const digits = getDigits(newValue);

                if (!isDeleting && digits.length > MAX_PHONE_DIGITS) {
                    return;
                }

                handlePhoneInput(newValue);

                return;
            }

            if (newValue.length > 0 && !/[^0-9+\-()\s]/.test(newValue)) {
                const digits = newValue.replace(/\D/g, '');

                if (isPhoneFormat(newValue)) {
                    handlePhoneInput(newValue);
                    setPhoneDigitsIfEmpty(digits);

                    return;
                }
            }

            setEmailType(newValue);
        },
        [fieldType, onChange, inputValue, phoneDigits, forcedFieldType, onClearError]
    );

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text');

        if (forcedFieldType === FieldType.PHONE || forcedFieldType === FieldType.CONTACT_PHONE) {
            if (isPhoneFormat(pastedText)) {
                if (!checkPhoneLength(pastedText, fieldType)) {
                    return;
                }
                handlePhoneInput(pastedText);
                setPhoneDigitsIfEmpty(pastedText);
            }

            return;
        }

        if (forcedFieldType === FieldType.EMAIL || forcedFieldType === FieldType.CONTACT_EMAIL) {
            setEmailType(pastedText);

            return;
        }

        if (isPhoneFormat(pastedText)) {
            if (!checkPhoneLength(pastedText, fieldType)) {
                return;
            }

            handlePhoneInput(pastedText);

            setPhoneDigitsIfEmpty(pastedText);
        } else if (pastedText.length > 0 && !/[^0-9+\-()\s]/.test(pastedText)) {
            const digits = pastedText.replace(/\D/g, '');

            if (!checkPhoneLength(pastedText, fieldType)) {
                return;
            }

            if (isPhoneFormat(pastedText)) {
                handlePhoneInput(pastedText);

                setPhoneDigitsIfEmpty(digits);
            } else {
                setEmailType(pastedText);
            }
        } else {
            setEmailType(pastedText);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const isPhoneMode =
            fieldType === FieldType.PHONE ||
            fieldType === FieldType.CONTACT_PHONE ||
            forcedFieldType === FieldType.PHONE ||
            forcedFieldType === FieldType.CONTACT_PHONE;

        if (isPhoneMode && e.key >= '0' && e.key <= '9') {
            const input = e.currentTarget;
            const digits = getDigits(inputValue);

            const hasSelection = input.selectionStart !== input.selectionEnd;

            if (digits.length >= MAX_PHONE_DIGITS && !hasSelection) {
                e.preventDefault();

                return;
            }
        }

        if (e.key === '+') {
            if (
                forcedFieldType === FieldType.PHONE ||
                forcedFieldType === FieldType.CONTACT_PHONE ||
                forcedFieldType === FieldType.EMAIL ||
                forcedFieldType === FieldType.CONTACT_EMAIL
            ) {
                return;
            }

            setEmailType();

            return;
        }

        if (e.key >= '0' && e.key <= '9') {
            if (forcedFieldType === FieldType.PHONE || forcedFieldType === FieldType.CONTACT_PHONE) {
                return;
            }

            if (inputValue.length === 0 && e.key === '9') {
                if (phoneDigits === '') {
                    setPhoneDigits('9');
                }

                setFieldType(FieldType.PHONE);
            } else if (inputValue.length === 1 && (inputValue === '7' || inputValue === '8') && e.key === '9') {
                if (phoneDigits === '') {
                    setPhoneDigits(inputValue + e.key);
                }

                setFieldType(FieldType.PHONE);
            } else if (inputValue.length === 2 && inputValue === '+7' && e.key === '9') {
                if (phoneDigits === '') {
                    setPhoneDigits('+79');
                }

                setFieldType(FieldType.PHONE);
            }
        }
    };

    const handleClear = useCallback(() => {
        setInputValue('');
        setPhoneDigits('');
        onChange('');

        if (onClearError) {
            onClearError();
        }
    }, [onChange, onClearError]);

    useEffect(() => {
        if (forcedFieldType !== FieldType.AUTO) {
            setFieldType(forcedFieldType);

            return;
        }

        if (!inputValue || /[^0-9+\-()\s]/.test(inputValue)) {
            setEmailType();

            return;
        }

        setFieldType(determineFieldType(inputValue));
    }, [inputValue, determineFieldType, setPhoneDigitsIfEmpty, forcedFieldType]);

    useEffect(() => {
        if (name === FieldType.PHONE && value) {
            handlePhoneInput(value);
            setPhoneDigitsIfEmpty(value);
        }
    }, []);

    useEffect(() => {
        if (value === '') {
            setInputValue('');
            setPhoneDigits('');
        }
    }, [value]);

    const isContactLabel = forcedFieldType === FieldType.CONTACT_PHONE || forcedFieldType === FieldType.CONTACT_EMAIL;

    return (
        <div className={styles.container}>
            {!withoutLabel && (
                <div className={cn(styles.label, isContactLabel && styles.label_contact)}>
                    {getDefaultLabelFromUtils(forcedFieldType)}
                </div>
            )}
            <div
                className={cn(
                    styles.field,
                    error && styles.field_error,
                    theme === 'secondary' && styles.field_secondary
                )}
            >
                <input
                    autoFocus={autoFocus}
                    name={name}
                    type={getInputTypeFromUtils(forcedFieldType)}
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    disabled={disabled}
                    className={cn(styles.input, ((inputValue && !disabled) || isLoading) && styles.input_withIcon)}
                />
                {inputValue && !isLoading && !disabled && (
                    <div className={styles.icon} onClick={handleClear}>
                        <CrossIcon />
                    </div>
                )}
                {isLoading && (
                    <div className={styles.loader}>
                        <LoadingSpinner />
                    </div>
                )}
            </div>
        </div>
    );
};

export default PhoneEmailInput;
